import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Save, Trash2, Plus, X, Settings } from 'lucide-react';

export default function DivisionPointsTab({ 
  configs, 
  setConfigs, 
  onRefresh 
}) {
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDivisionName, setNewDivisionName] = useState('');
  const [newDivisionPoints, setNewDivisionPoints] = useState([7, 6, 5, 4, 3, 2, 1]);
  const [addingDivision, setAddingDivision] = useState(false);

  const handlePointsChange = (divisionIndex, pointIndex, value) => {
    const newConfigs = [...configs];
    newConfigs[divisionIndex].point_rules[pointIndex] = parseInt(value) || 0;
    setConfigs(newConfigs);
  };

  const handleSaveConfigs = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/division-configs`, { configs });
      toast.success('Division configurations saved successfully');
    } catch (error) {
      toast.error('Failed to save configurations');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDivision = async (divisionName) => {
    if (!window.confirm(`Are you sure you want to delete "${divisionName}" division? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/division-configs/${encodeURIComponent(divisionName)}`);
      toast.success(`Division "${divisionName}" deleted`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete division');
      console.error(error);
    }
  };

  const handleAddDivision = async (e) => {
    e.preventDefault();
    if (!newDivisionName.trim()) {
      toast.error('Please enter a division name');
      return;
    }
    
    setAddingDivision(true);
    try {
      await axios.post(`${API}/admin/division-configs`, {
        division: newDivisionName.trim(),
        points: newDivisionPoints
      });
      toast.success(`Division "${newDivisionName}" created`);
      setNewDivisionName('');
      setNewDivisionPoints([7, 6, 5, 4, 3, 2, 1]);
      setShowAddForm(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create division');
      console.error(error);
    } finally {
      setAddingDivision(false);
    }
  };

  const handleNewPointChange = (index, value) => {
    const updated = [...newDivisionPoints];
    updated[index] = parseInt(value) || 0;
    setNewDivisionPoints(updated);
  };

  const addPointSlot = () => {
    setNewDivisionPoints([...newDivisionPoints, 1]);
  };

  const removePointSlot = (index) => {
    if (newDivisionPoints.length > 1) {
      setNewDivisionPoints(newDivisionPoints.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Division Button */}
      <div className="flex justify-end">
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm uppercase tracking-wider font-bold font-barlow"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Division
        </Button>
      </div>

      {/* Add Division Form */}
      {showAddForm && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6">
          <h3 className="font-barlow font-bold text-lg text-slate-900 uppercase tracking-tight mb-4">
            Create New Division
          </h3>
          <form onSubmit={handleAddDivision} className="space-y-4">
            <div>
              <Label htmlFor="new-division-name" className="font-inter font-medium text-slate-900">
                Division Name
              </Label>
              <Input
                id="new-division-name"
                type="text"
                value={newDivisionName}
                onChange={(e) => setNewDivisionName(e.target.value)}
                className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter"
                placeholder="e.g., Limited, Classic, etc."
                required
              />
            </div>

            <div>
              <Label className="font-inter font-medium text-slate-900">
                Point Rules (1st place, 2nd place, ...)
              </Label>
              <div className="flex flex-wrap gap-2 mt-2 items-center">
                {newDivisionPoints.map((points, idx) => (
                  <div key={`point-slot-${idx}`} className="flex items-center gap-1">
                    <span className="text-xs text-stone-500 font-mono w-6">{idx + 1}:</span>
                    <Input
                      type="number"
                      value={points}
                      onChange={(e) => handleNewPointChange(idx, e.target.value)}
                      className="w-16 h-10 text-center font-mono bg-white border-stone-300 rounded-sm"
                      min="0"
                    />
                    {newDivisionPoints.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePointSlot(idx)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={addPointSlot}
                  variant="outline"
                  className="h-10 px-3 rounded-sm"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-stone-500 mt-2 font-inter">
                Points awarded to each position. Add more slots for larger fields.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={addingDivision}
                className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm uppercase tracking-wider font-bold font-barlow"
              >
                {addingDivision ? 'Creating...' : 'Create Division'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddForm(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Existing Divisions */}
      {configs.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <Settings className="w-16 h-16 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-barlow text-xl text-slate-900">No divisions configured</p>
          <p className="font-inter text-stone-600 mt-2">Click &quot;Add Division&quot; to create your first division.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {configs.map((config, divIndex) => (
            <div key={config.division} className="bg-white border border-stone-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-barlow font-bold text-lg text-slate-900 uppercase tracking-tight">
                  {config.division}
                </h3>
                <Button
                  variant="ghost"
                  onClick={() => handleDeleteDivision(config.division)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-3">
                {config.point_rules.map((points, pointIndex) => (
                  <div key={pointIndex} className="flex flex-col items-center">
                    <span className="text-xs text-stone-500 font-inter mb-1">
                      {pointIndex + 1}{pointIndex === 0 ? 'st' : pointIndex === 1 ? 'nd' : pointIndex === 2 ? 'rd' : 'th'}
                    </span>
                    <Input
                      type="number"
                      value={points}
                      onChange={(e) => handlePointsChange(divIndex, pointIndex, e.target.value)}
                      className="w-16 h-12 text-center font-mono text-lg bg-white border-stone-300 rounded-sm"
                      min="0"
                    />
                    <span className="text-xs text-stone-500 font-inter mt-1">pts</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save Button */}
      {configs.length > 0 && (
        <div className="flex justify-end">
          <Button
            onClick={handleSaveConfigs}
            disabled={saving}
            className="bg-amber-600 text-white hover:bg-amber-700 rounded-sm uppercase tracking-wider font-bold font-barlow"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      )}
    </div>
  );
}
